import React from "react"
import cx from "classnames"
import { Icon } from "../icon"
import { Menu } from "../menu"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderMenuItem = {
  /** Stable key for the item. */
  key: string
  /** Visible label. The active item's label is shown in the collapsed trigger. */
  label: React.ReactNode
  /** Whether this item is currently active. */
  isActive?: boolean
  /**
   * Element to render the item as (e.g. an `<a>` or framework `Link`). When
   * omitted, the item renders as Base UI's default `Menu.Item` and relies on
   * `onClick`.
   */
  render?: React.ReactElement
  /** Click handler. Used to select tabs or trigger navigation buttons. */
  onClick?: (event: React.MouseEvent) => void
}

export type PageSecondaryHeaderMenuProps = {
  /**
   * Accessible name for the collapsed trigger, providing context beyond the
   * active item's label (e.g. "Form modes", "Steps").
   */
  accessibleName: string
  /** Items rendered in the dropdown. */
  items: PageSecondaryHeaderMenuItem[]
  /** Called when the menu opens or closes. */
  onOpenChange?: (open: boolean) => void
  /** `data-slot` for the wrapper element. */
  dataSlot?: string
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const containerStyles = [
  "flex items-center justify-center",
  "@6xl/page-header:hidden"
]

const triggerStyles = [
  "py-1 px-2",
  "text-sm leading-6 font-semibold text-gray-700 text-nowrap",
  "transition-colors duration-200",
  "hover:text-gray-900 data-[popup-open]:text-gray-900"
]

// =============================================================================
// Helpers
// =============================================================================

/**
 * Base UI merges a `render` element's own `children` over the component's
 * children, which would drop our `Menu.ItemLabel`/`Menu.ItemTrailing`. Rebuild
 * the element without children so the menu item stays consistently structured
 * while keeping navigation props (`href`, `onClick`, etc.).
 */
function withoutChildren(element: React.ReactElement): React.ReactElement {
  const { children: _children, ...props } = element.props as Record<
    string,
    unknown
  >
  return React.createElement(element.type, props)
}

// =============================================================================
// Component
// =============================================================================

/**
 * Collapsed dropdown shown until `@3xl/page-header` for the secondary page
 * header's center content (tabs, tab links, steps). Renders a custom
 * `Menu.Trigger` showing the active item's label, and a `Menu.Item` per item.
 *
 * Internal helper composed by `PageSecondaryHeader.Tabs`,
 * `PageSecondaryHeader.TabLinks`, and `PageSecondaryHeader.Steps` — not part of
 * the public `PageSecondaryHeader.*` API.
 */
export function PageSecondaryHeaderMenu({
  accessibleName,
  items,
  onOpenChange,
  dataSlot = "secondary-menu",
  className
}: PageSecondaryHeaderMenuProps) {
  const activeItem = items.find(item => item.isActive) ?? items[0]

  return (
    <div className={cx(containerStyles, className)} data-slot={dataSlot}>
      <Menu
        onOpenChange={onOpenChange ? open => onOpenChange(open) : undefined}
      >
        <Menu.Trigger
          accessibleName={accessibleName}
          render={<button type="button" className={cx(triggerStyles)} />}
        >
          {activeItem?.label}
          <Icon name="down" size="xs" />
        </Menu.Trigger>
        <Menu.Content align="center">
          {items.map(item => (
            <Menu.Item
              key={item.key}
              render={item.render ? withoutChildren(item.render) : undefined}
              onClick={item.onClick}
            >
              <Menu.ItemLabel>{item.label}</Menu.ItemLabel>
              {item.isActive && (
                <Menu.ItemTrailing>
                  <Icon name="check" size="xs" />
                </Menu.ItemTrailing>
              )}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu>
    </div>
  )
}

PageSecondaryHeaderMenu.displayName = "PageSecondaryHeaderMenu"
